import React, { useState, useEffect, useCallback } from "react";
import DataTable from "../../components/DataTable";
import { useNavigate } from "react-router-dom";
import { getCustomerSalesInstallment } from "../../services/sales";
import { useUser } from "../../context/UserContext";
import axiosInstance from "../../services/api/axios";
import { CheckCircle2, CircleAlert, CircleX, Eye, List, ChartNoAxesCombined } from "lucide-react";
import { SecondaryButton } from "../../components/Buttons";
import SummaryCard from "../../components/SummaryCard";
import { fetchProjects } from "../../services/projects";
import usePagination from "../../hooks/usePagination";
import PaginationControls from "../../components/PaginationControls";
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import SearchInput from "../../components/SearchInput";

function SalesDashboard() {
  const [selectedProject, setSelectedProject] = useState("");
  const [projects, setProjects] = useState([]);
  const [cancellationFilter, setCancellationFilter] = useState("all");
  const navigate = useNavigate();
  const { userInfo } = useUser();

  const customerId = userInfo?.loginIdentifierDetails?.customer?.customerId;

  // Create fetch function for sales with pagination
  const fetchSalesBackend = useCallback(async ({ page, page_size, search }) => {
    let params = { page, page_size };

    if (userInfo?.persona === "STAFF") {
      params.salesLoginId = userInfo.loginId;
    } else {
      params.customerId = customerId;
    }

    if (search) params.search = search;

    try {
      const salesResponse = await axiosInstance.get("/customer/sales", { params });
      
      // Handle both paginated and non-paginated responses
      let salesArray;
      if (salesResponse?.items && salesResponse.total !== undefined) {
        salesArray = salesResponse.items;
      } else if (Array.isArray(salesResponse)) {
        salesArray = salesResponse;
      } else {
        salesArray = [];
      }

      // Fetch installments for all sales
      const installmentsResponse = await getCustomerSalesInstallment({ customerId: customerId });
      const installmentsArray = installmentsResponse?.items || (Array.isArray(installmentsResponse) ? installmentsResponse : []);
      
      const salesDataWithInstallments = salesArray.map((sale) => ({
        ...sale,
        installments: installmentsArray.filter(
          (installment) =>
            installment.customerSales?.customerSalesId ===
            sale.customerSalesId
        ),
      }));

      // Return paginated response format
      if (salesResponse?.items && salesResponse.total !== undefined) {
        return {
          ...salesResponse,
          items: salesDataWithInstallments
        };
      } else {
        return {
          items: salesDataWithInstallments,
          total: salesDataWithInstallments.length,
          page: 1,
          page_size: salesDataWithInstallments.length,
          total_pages: 1,
          has_next: false,
          has_previous: false
        };
      }
    } catch (error) {
      console.error("Error fetching sales data:", error);
      return { items: [], total: 0, page: 1, page_size: 30, total_pages: 1, has_next: false, has_previous: false };
    }
  }, [userInfo, customerId]);

  // Use the pagination hook with manual search
  const {
    data: salesData,
    pagination,
    searchInput,
    loading,
    error,
    goToPage,
    changePageSize,
    handleSearchInputChange,
    triggerSearch,
    refetch
  } = usePagination(fetchSalesBackend, 30, '', true, true);

  // Track if this is the initial load
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  
  useEffect(() => {
    if (!loading && salesData.length > 0) {
      setIsInitialLoad(false);
    }
  }, [loading, salesData.length]);

  // Note: Client-side filters (project and cancellation) are applied after fetching
  // No refetch needed as the backend doesn't support these filters yet
  // useEffect(() => {
  //   refetch();
  // }, [selectedProject, cancellationFilter, refetch]);
  const handleCancellationFilterChange = (e) => {
    setCancellationFilter(e.target.value);
  };

  // Client-side filtering for cancellation and project filters only
  const filteredSales = salesData.filter((sale) => {
    let cancellationMatch = true;
    let projectMatch = true;

    if (selectedProject) {
      projectMatch = sale.propertyData?.projectPhase?.project?.projectId === Number(selectedProject);
    }

    if (cancellationFilter === "initiated") {
      cancellationMatch = sale.cancellationStatus === "Initiated";
    } else if (cancellationFilter === "cancelled") {
      cancellationMatch = sale.cancellationStatus === "Cancelled";
    } else if (cancellationFilter === "notInitiated") {
      cancellationMatch = sale.cancellationStatus === null;
    }

    return cancellationMatch && projectMatch;
  });
  // Fetch projects
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetchProjects({ customerId: customerId });
        setProjects(response);
      } catch (error) {
        console.error("Error fetching projects:", error);
      }
    };
    fetchData();
  }, []);

  const totalSales = salesData.length;
  const activeSales = salesData.filter(
    (sale) => sale.cancellationStatus === null
  ).length;
  const initiatedCancellations = salesData.filter(
    (sale) => sale.cancellationStatus === "Initiated"
  ).length;
  const cancelledSales = salesData.filter(
    (sale) => sale.cancellationStatus === "Completed"
  ).length;
  const handoverProperties = salesData.filter((sale) => sale.isHandover).length;
  const nonHandoverProperties = salesData.filter(
    (sale) => !sale.isHandover
  ).length;

  const handleView = (data) => {
    const projectId = data.propertyData?.projectPhase?.project?.projectId;
    navigate(`/properties/sold/${data.customerSalesId}?projectId=${projectId}`);
  };

  // ActionsCellRenderer + dateFormatter removed — the DataTable columns now
  // render the View button + formatted sale date inline.

  const _unused = (params) => {
    if (params.value) {
      try {
        return new Date(params.value).toLocaleDateString();
      } catch (err) {
        return null;
      }
    }
    return null;
  };

  const columns = [
    { key: "project", header: "Project", width: 180, render: (r) => (
      <span className="text-sm font-medium text-slate-900 truncate block" title={r.projectName}>
        {r.projectName || "—"}
      </span>
    )},
    { key: "building", header: "Bldg.", width: 80, render: (r) => (
      <span className="text-sm text-slate-700 tabular-nums">
        {r?.propertyData?.buildingNo || "—"}
      </span>
    )},
    { key: "unit", header: "Unit", width: 80, render: (r) => (
      <span className="text-sm font-semibold text-slate-900 tabular-nums">
        {r?.propertyData?.propertyNo || "—"}
      </span>
    )},
    { key: "agent", header: "Channel partner", width: 160, render: (r) => (
      <span className="text-sm text-slate-700 truncate block"
        title={r.agent?.ownerName || ""}>
        {r.agent?.ownerName || "—"}
      </span>
    )},
    { key: "customer", header: "Customer", width: 180, render: (r) => {
      const name = `${r.user?.firstName || ""} ${r.user?.lastName || ""}`.trim();
      return (
        <span className="text-sm font-medium text-slate-900 truncate block" title={name}>
          {name || "—"}
        </span>
      );
    }},
    { key: "phone", header: "Phone", width: 140, render: (r) => (
      <span className="text-sm text-slate-700 tabular-nums">
        {r.user?.mobileNoCalling || "—"}
      </span>
    )},
    { key: "cancellation", header: "Cancellation", width: 150, render: (r) => {
      const v = r.cancellationStatus;
      const statusText = v === null ? "Active" : v === "Initiated" ? "Initiated" : "Completed";
      const cls = v === null
        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
        : v === "Initiated"
        ? "bg-amber-50 text-amber-700 border-amber-200"
        : "bg-rose-50 text-rose-700 border-rose-200";
      return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold border ${cls}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${
            v === null ? "bg-emerald-500" : v === "Initiated" ? "bg-amber-500" : "bg-rose-500"
          }`} />
          {statusText.toUpperCase()}
        </span>
      );
    }},
    { key: "handover", header: "Handover", width: 100, render: (r) => (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium ${
        r.isHandover
          ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
          : "bg-slate-100 text-slate-600 border border-slate-200"
      }`}>
        {r.isHandover ? "Yes" : "No"}
      </span>
    )},
    { key: "saleDate", header: "Sale date", width: 130, render: (r) => (
      <span className="text-xs text-slate-600 tabular-nums">
        {r.saleDate ? new Date(r.saleDate).toLocaleDateString("en-IN", {
          day: "numeric", month: "short", year: "numeric"
        }) : "—"}
      </span>
    )},
    { key: "actions", header: "View", width: 70, align: "center", render: (r) => (
      <button
        type="button"
        onClick={() => handleView(r)}
        className="inline-flex items-center justify-center w-8 h-8 rounded-md text-slate-500 hover:text-flamingo-600 hover:bg-flamingo-50 transition-colors"
        title="View sale"
        aria-label="View sale"
      >
        <Eye size={14} />
      </button>
    )},
  ];

  // Skeleton component for SummaryCard
  const SummaryCardSkeleton = () => (
    <div className="bg-white/90 backdrop-blur-sm p-6 rounded-lg shadow-sm border border-gray-100/50 h-32">
      <div className="flex items-center gap-6 h-full">
        <div className="flex justify-center items-center w-12 h-12 rounded-lg bg-gradient-to-br from-gray-200 to-gray-300">
          <Skeleton circle height={22} width={22} />
        </div>
        <div className="flex-1 min-w-0">
          <Skeleton height={12} width="60%" className="mb-3" />
          <div className="flex items-baseline gap-3">
            <Skeleton height={32} width="80px" />
            <Skeleton height={16} width="30px" />
          </div>
        </div>
      </div>
    </div>
  );

  // Skeleton component for AG Grid rows overlay
  const GridSkeletonOverlay = () => (
    <div className="absolute inset-0 bg-white/70 backdrop-blur-sm flex items-center justify-center z-10">
      <div className="flex items-center gap-3">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="text-gray-600 text-sm">Loading...</span>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col gap-6 min-h-[calc(100vh-12rem)]">
      {/* Hero band */}
      <div className="rounded-2xl shadow-sm bg-gradient-to-r from-slate-800 via-slate-700 to-slate-600 p-5 lg:p-6 text-white">
        <p className="text-xs uppercase tracking-wider text-slate-300 flex items-center gap-1.5">
          <ChartNoAxesCombined size={12} />
          Sales
        </p>
        <h1 className="text-xl sm:text-2xl font-semibold tracking-tight mt-1">
          Customer sales
        </h1>
        <p className="text-xs text-slate-300 mt-1">
          Bookings, cancellations, and handover status across all projects
        </p>
      </div>

      {/* KPI tiles — sale-status breakdown. */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {loading && isInitialLoad ? (
          <>
            <SummaryCardSkeleton />
            <SummaryCardSkeleton />
            <SummaryCardSkeleton />
            <SummaryCardSkeleton />
          </>
        ) : (
          <>
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center shrink-0">
                <List size={20} />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">Total sales</p>
                <p className="text-2xl font-bold text-slate-900 tabular-nums">{totalSales}</p>
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                <CheckCircle2 size={20} />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">Active</p>
                <p className="text-2xl font-bold text-slate-900 tabular-nums">{activeSales}</p>
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-700 flex items-center justify-center shrink-0">
                <CircleAlert size={20} />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">Cancellation initiated</p>
                <p className="text-2xl font-bold text-slate-900 tabular-nums">{initiatedCancellations}</p>
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center shrink-0">
                <CircleX size={20} />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">Cancelled</p>
                <p className="text-2xl font-bold text-slate-900 tabular-nums">{cancelledSales}</p>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Filter strip — slate look, same as DashboardContent. */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <div className="flex-1 min-w-[200px]">
            <SearchInput
              value={searchInput}
              onChange={handleSearchInputChange}
              onSearch={triggerSearch}
              placeholder="Search by sale date or customer ID…"
              disabled={loading}
              className="w-full"
            />
          </div>
          <select
            value={selectedProject}
            onChange={(e) => setSelectedProject(e.target.value)}
            className="px-3 py-2 text-sm rounded-lg border border-slate-300 bg-white focus:border-flamingo-500 focus:ring-1 focus:ring-flamingo-200 outline-none min-w-[180px]"
            disabled={loading}
          >
            <option value="">All projects</option>
            {projects.map((project) => (
              <option key={project.projectId} value={project.projectId}>
                {project.projectName}
              </option>
            ))}
          </select>
          <select
            value={cancellationFilter}
            onChange={handleCancellationFilterChange}
            className="px-3 py-2 text-sm rounded-lg border border-slate-300 bg-white focus:border-flamingo-500 focus:ring-1 focus:ring-flamingo-200 outline-none min-w-[180px]"
            disabled={loading}
          >
            <option value="all">All statuses</option>
            <option value="notInitiated">Active</option>
            <option value="initiated">Cancellation initiated</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {/* Sales table */}
      <DataTable
        columns={columns}
        data={filteredSales}
        loading={loading && isInitialLoad}
        refetching={loading && !isInitialLoad}
        minWidth={1280}
        rowKey={(r, i) => r?.customerSalesId ?? i}
        emptyState={
          <div className="px-6 py-12 text-center">
            <p className="text-base font-semibold text-slate-900">
              {searchInput || selectedProject
                ? "No sales match your filters"
                : "No sales yet"}
            </p>
            <p className="text-sm text-slate-500 mt-1">
              {searchInput || selectedProject
                ? "Try adjusting your search terms or filters."
                : "Sales will appear here as bookings are recorded."}
            </p>
          </div>
        }
      />
      {/* Pagination pinned to the bottom of the page area via mt-auto, so it
          sits at the bottom whether there are 2 sales or 50 — same pattern as
          the Projects card screen. */}
      {!loading && !error && pagination.total > 0 && (
        <div className="mt-auto pt-4 border-t border-slate-200">
          <PaginationControls
            pagination={pagination}
            onPageChange={goToPage}
            onPageSizeChange={changePageSize}
            itemName="sales"
            variant="list"
          />
        </div>
      )}
    </div>
  );
}

export default SalesDashboard;